import type { DossierIntakeInput } from "@shared/routes";
import type { ServiceType } from "@shared/pre-analise/types";
import { isActiveService, SERVICE_DEFINITIONS } from "@shared/pre-analise/services";

/** Estado do wizard de abertura (F01). Strings vazias = não informado. */
export type DossierFormState = {
  objetoTipo: "veiculo" | "imovel" | "outro";
  solicitanteId: string;
  // Cliente e solicitação (F01 1–10)
  clienteEntidade: string; entidadeTipo: string; entidadeCnpj: string; unidadeFilial: string;
  profissionalSolicitante: string; solicitanteSetor: string; solicitanteEmail: string; solicitanteTelefone: string;
  solicitadoEm: string; prazoSolicitado: string;
  // Sinistro e cobertura (11–20)
  numeroSinistro: string; numeroApolice: string; seguradoNome: string; seguradoDocumento: string;
  vigenciaInicio: string; vigenciaFim: string; coberturaAcionada: string; franquiaParticipacao: string;
  regulacaoStatus: string; valorReclamado: string;
  // Evento (21–28)
  dataSinistro: string; eventoHorario: string; eventoMunicipio: string; eventoUf: string; eventoEndereco: string;
  eventoLocalizacao: string; eventoOrgaoRegistro: string; eventoNaturezas: string[]; descricaoEvento: string;
  // Objeto e envolvidos (29–30 + dados veiculares do ReMonta)
  veiculoId: string; classificacaoOriginal: string; ufRegistro: string; numeroBat: string;
  valorFipe: string; mesReferenciaFipe: string; veiculosEnvolvidos: string; bensTerceirosEnvolvidos: string;
  // Primeiro serviço e atendimento técnico (31–34, 37)
  primeiroServico: ServiceType | "";
  numeroOrdemExterna: string; reguladorExterno: string; vistoriaPrevistaEm: string; prazoEntregaEm: string;
  // Contatos, documentos e responsável (35–36, 38–42)
  contatoAcessoNome: string; contatoAcessoTelefone: string; canalEntrega: string; documentosIniciaisReferencia: string;
  responsavelInformacoesNome: string; responsavelInformacoesCargo: string; autorizadoEnvio: boolean;
};

export type DossierFormErrors = Partial<Record<keyof DossierFormState, string>>;

/** Rótulos dos campos, usados nos inputs e na revisão. */
export const DOSSIER_FIELD_LABELS: Record<Exclude<keyof DossierFormState, "objetoTipo">, string> = {
  solicitanteId: "Solicitante", clienteEntidade: "Cliente/entidade", entidadeTipo: "Tipo de entidade", entidadeCnpj: "CNPJ",
  unidadeFilial: "Unidade/filial", profissionalSolicitante: "Profissional solicitante", solicitanteSetor: "Setor",
  solicitanteEmail: "E-mail", solicitanteTelefone: "Telefone", solicitadoEm: "Data da solicitação", prazoSolicitado: "Prazo solicitado",
  numeroSinistro: "Número do sinistro", numeroApolice: "Número da apólice/certificado", seguradoNome: "Segurado/associado/cooperado",
  seguradoDocumento: "CPF/CNPJ do segurado", vigenciaInicio: "Início da vigência", vigenciaFim: "Final da vigência",
  coberturaAcionada: "Cobertura acionada", franquiaParticipacao: "Franquia/participação", regulacaoStatus: "Status da regulação",
  valorReclamado: "Valor inicialmente reclamado", dataSinistro: "Data do evento", eventoHorario: "Horário aproximado",
  eventoMunicipio: "Município", eventoUf: "UF", eventoEndereco: "Local/endereço", eventoLocalizacao: "Latitude/longitude",
  eventoOrgaoRegistro: "Órgão de registro", eventoNaturezas: "Natureza do evento",
  descricaoEvento: "Descrição resumida da ocorrência e dinâmica informada", veiculoId: "Veículo",
  classificacaoOriginal: "Monta original", ufRegistro: "UF de registro", numeroBat: "Número do BAT", valorFipe: "Valor FIPE",
  mesReferenciaFipe: "Referência FIPE", veiculosEnvolvidos: "Veículos envolvidos",
  bensTerceirosEnvolvidos: "Bens, estruturas e terceiros envolvidos", primeiroServico: "Serviço de interesse (opcional)",
  numeroOrdemExterna: "Ordem de serviço do cliente", reguladorExterno: "Regulador/perito do cliente",
  vistoriaPrevistaEm: "Data prevista da vistoria", prazoEntregaEm: "Prazo do laudo/relatório",
  contatoAcessoNome: "Contato para acesso ao bem", contatoAcessoTelefone: "Telefone para acesso", canalEntrega: "Canal de entrega",
  documentosIniciaisReferencia: "Link, pasta ou protocolo dos documentos iniciais", responsavelInformacoesNome: "Nome",
  responsavelInformacoesCargo: "Cargo/setor", autorizadoEnvio: "Autorização para o envio",
};

export const DOSSIER_STEPS = [
  { id: "cliente", title: "Cliente e solicitação" },
  { id: "sinistro", title: "Sinistro e cobertura" },
  { id: "evento", title: "Dados do evento" },
  { id: "envolvidos", title: "Objeto e envolvidos" },
  { id: "servico", title: "Objetivo da solicitação" },
  { id: "contatos", title: "Contatos e documentos" },
  { id: "revisao", title: "Revisão" },
] as const;

/** Opções canônicas do F01. */
export { ENTIDADE_TIPOS, REGULACAO_STATUS, EVENTO_NATUREZAS, CANAIS_ENTREGA, UFS } from "@shared/pre-analise/field-options";
export const SERVICE_LABELS: Record<ServiceType, string> = {
  nexo_causal: SERVICE_DEFINITIONS.nexo_causal.label,
  sindicancia: SERVICE_DEFINITIONS.sindicancia.label,
  orcamento_tecnico: SERVICE_DEFINITIONS.orcamento_tecnico.label,
  sindicancia_nexo: "Sindicância e nexo causal",
  auditoria_danos_valores: "Auditoria técnica de danos e valores",
  pericia_bens_imoveis: "Perícia de danos em bens imóveis",
  auditoria_oficinas: "Auditoria técnica de oficinas",
  reclassificacao_monta: "Reclassificação de monta",
};

export function emptyDossierForm(): DossierFormState {
  return {
    objetoTipo: "veiculo", solicitanteId: "",
    clienteEntidade: "", entidadeTipo: "", entidadeCnpj: "", unidadeFilial: "", profissionalSolicitante: "",
    solicitanteSetor: "", solicitanteEmail: "", solicitanteTelefone: "", solicitadoEm: "", prazoSolicitado: "",
    numeroSinistro: "", numeroApolice: "", seguradoNome: "", seguradoDocumento: "", vigenciaInicio: "", vigenciaFim: "",
    coberturaAcionada: "", franquiaParticipacao: "", regulacaoStatus: "", valorReclamado: "",
    dataSinistro: "", eventoHorario: "", eventoMunicipio: "", eventoUf: "", eventoEndereco: "", eventoLocalizacao: "",
    eventoOrgaoRegistro: "", eventoNaturezas: [], descricaoEvento: "",
    veiculoId: "", classificacaoOriginal: "", ufRegistro: "", numeroBat: "", valorFipe: "", mesReferenciaFipe: "",
    veiculosEnvolvidos: "", bensTerceirosEnvolvidos: "",
    primeiroServico: "", numeroOrdemExterna: "", reguladorExterno: "", vistoriaPrevistaEm: "", prazoEntregaEm: "",
    contatoAcessoNome: "", contatoAcessoTelefone: "", canalEntrega: "", documentosIniciaisReferencia: "",
    responsavelInformacoesNome: "", responsavelInformacoesCargo: "", autorizadoEnvio: false,
  };
}

const filled = (value: string) => value.trim().length > 0;
const EMAIL = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
const required = (state: DossierFormState, errors: DossierFormErrors, fields: (keyof DossierFormState)[]) => {
  for (const field of fields) if (!filled(String(state[field] ?? ""))) errors[field] = "Campo obrigatório";
};

/** Erros da etapa `step` (índice em DOSSIER_STEPS). `inspector`: vistoriadora escolhe o Solicitante. */
export function validateDossierStep(state: DossierFormState, step: number, options: { inspector?: boolean } = {}): DossierFormErrors {
  const errors: DossierFormErrors = {};
  switch (DOSSIER_STEPS[step]?.id) {
    case "cliente":
      if (options.inspector) required(state, errors, ["solicitanteId"]);
      required(state, errors, ["clienteEntidade", "entidadeTipo", "profissionalSolicitante", "solicitanteSetor", "solicitanteEmail", "solicitanteTelefone", "solicitadoEm"]);
      if (filled(state.solicitanteEmail) && !EMAIL.test(state.solicitanteEmail.trim())) errors.solicitanteEmail = "E-mail inválido";
      break;
    case "sinistro":
      required(state, errors, ["numeroSinistro", "seguradoNome"]);
      if (state.vigenciaInicio && state.vigenciaFim && state.vigenciaInicio > state.vigenciaFim) errors.vigenciaFim = "Fim da vigência anterior ao início";
      break;
    case "evento":
      required(state, errors, ["dataSinistro", "eventoMunicipio", "eventoUf", "eventoEndereco", "descricaoEvento"]);
      if (!state.eventoNaturezas.length) errors.eventoNaturezas = "Selecione ao menos uma natureza";
      break;
    case "envolvidos":
      if (state.objetoTipo === "veiculo") {
        required(state, errors, ["veiculoId", "classificacaoOriginal"]);
        if (!/^[A-Za-z]{2}$/.test(state.ufRegistro.trim())) errors.ufRegistro = "Informe a UF de registro";
      }
      if (state.objetoTipo === "veiculo" && filled(state.valorFipe) !== filled(state.mesReferenciaFipe)) {
        errors[filled(state.valorFipe) ? "mesReferenciaFipe" : "valorFipe"] = "Informe valor e mês de referência juntos";
      }
      break;
    case "servico":
      if (state.primeiroServico && !isActiveService(state.primeiroServico)) errors.primeiroServico = "Escolha um dos quatro serviços disponíveis ou deixe a definição para a pré-análise";
      else if (state.primeiroServico === "reclassificacao_monta" && state.objetoTipo !== "veiculo") {
        errors.primeiroServico = "Reclassificação de monta exige um veículo como objeto";
      }
      break;
    case "contatos":
      required(state, errors, ["responsavelInformacoesNome", "responsavelInformacoesCargo"]);
      if (!state.autorizadoEnvio) errors.autorizadoEnvio = "Confirme a autorização para o envio";
      break;
  }
  return errors;
}

export function firstInvalidStep(state: DossierFormState, options: { inspector?: boolean } = {}): number | null {
  for (let step = 0; step < DOSSIER_STEPS.length; step++) {
    if (Object.keys(validateDossierStep(state, step, options)).length) return step;
  }
  return null;
}

const text = (value: string) => (filled(value) ? value.trim() : undefined);
function compact<T extends Record<string, unknown>>(input: T): Partial<T> {
  return Object.fromEntries(Object.entries(input).filter(([, value]) => value !== undefined)) as Partial<T>;
}

/** Payload de envio (modo "enviar") com a modalidade; campos vazios e veiculares fora do objeto ficam de fora. */
export function buildDossierPayload(state: DossierFormState, options: { inspector?: boolean } = {}): DossierIntakeInput {
  const vehicle = state.objetoTipo === "veiculo";
  const primeiraOrdem = compact({
    numeroOrdemExterna: text(state.numeroOrdemExterna), reguladorExterno: text(state.reguladorExterno),
    vistoriaPrevistaEm: text(state.vistoriaPrevistaEm), prazoEntregaEm: text(state.prazoEntregaEm),
  });
  return compact({
    objetoTipo: state.objetoTipo,
    modo: "enviar" as const,
    primeiroServico: state.primeiroServico || undefined,
    solicitanteId: options.inspector ? text(state.solicitanteId) : undefined,
    clienteEntidade: text(state.clienteEntidade), entidadeTipo: text(state.entidadeTipo), entidadeCnpj: text(state.entidadeCnpj),
    unidadeFilial: text(state.unidadeFilial), profissionalSolicitante: text(state.profissionalSolicitante),
    solicitanteSetor: text(state.solicitanteSetor), solicitanteEmail: text(state.solicitanteEmail),
    solicitanteTelefone: text(state.solicitanteTelefone), solicitadoEm: text(state.solicitadoEm), prazoSolicitado: text(state.prazoSolicitado),
    numeroSinistro: text(state.numeroSinistro), numeroApolice: text(state.numeroApolice), seguradoNome: text(state.seguradoNome),
    seguradoDocumento: text(state.seguradoDocumento), vigenciaInicio: text(state.vigenciaInicio), vigenciaFim: text(state.vigenciaFim),
    coberturaAcionada: text(state.coberturaAcionada), franquiaParticipacao: text(state.franquiaParticipacao),
    regulacaoStatus: text(state.regulacaoStatus), valorReclamado: text(state.valorReclamado),
    dataSinistro: text(state.dataSinistro), eventoHorario: text(state.eventoHorario), eventoMunicipio: text(state.eventoMunicipio),
    eventoUf: text(state.eventoUf), eventoEndereco: text(state.eventoEndereco), eventoLocalizacao: text(state.eventoLocalizacao),
    eventoOrgaoRegistro: text(state.eventoOrgaoRegistro), eventoNaturezas: state.eventoNaturezas.length ? [...state.eventoNaturezas] : undefined,
    descricaoEvento: text(state.descricaoEvento),
    veiculosEnvolvidos: text(state.veiculosEnvolvidos), bensTerceirosEnvolvidos: text(state.bensTerceirosEnvolvidos),
    veiculoId: vehicle ? text(state.veiculoId) : undefined,
    classificacaoOriginal: vehicle ? text(state.classificacaoOriginal) : undefined,
    ufRegistro: vehicle ? text(state.ufRegistro)?.toUpperCase() : undefined,
    numeroBat: vehicle ? text(state.numeroBat) : undefined,
    valorFipe: vehicle ? text(state.valorFipe) : undefined,
    mesReferenciaFipe: vehicle && filled(state.mesReferenciaFipe) ? `${state.mesReferenciaFipe.trim()}-01` : undefined,
    primeiraOrdem: Object.keys(primeiraOrdem).length ? primeiraOrdem : undefined,
    contatoAcessoNome: text(state.contatoAcessoNome), contatoAcessoTelefone: text(state.contatoAcessoTelefone),
    canalEntrega: text(state.canalEntrega), documentosIniciaisReferencia: text(state.documentosIniciaisReferencia),
    responsavelInformacoesNome: text(state.responsavelInformacoesNome), responsavelInformacoesCargo: text(state.responsavelInformacoesCargo),
    autorizadoEnvio: state.autorizadoEnvio,
  }) as DossierIntakeInput;
}

const DRAFT_VERSION = 1;

/** Rascunho local por empresa + usuário: nunca compartilhado entre contas no mesmo navegador. */
export function draftStorageKey(userId: string, empresaId: string): string {
  return `remonta:dossie-draft:v${DRAFT_VERSION}:${empresaId}:${userId}`;
}

/** Remove todos os rascunhos de Dossiê (contêm dados pessoais); chamado no logout. */
export function clearDossierDrafts(
  storage: Pick<Storage, "length" | "key" | "removeItem"> | undefined = typeof window === "undefined" ? undefined : window.localStorage,
): void {
  try {
    if (!storage) return;
    const keys: string[] = [];
    for (let index = 0; index < storage.length; index++) {
      const key = storage.key(index);
      if (key?.startsWith("remonta:dossie-draft:")) keys.push(key);
    }
    keys.forEach((key) => storage.removeItem(key));
  } catch { /* storage bloqueado: nada a limpar */ }
}

export function serializeDossierDraft(state: DossierFormState): string {
  return JSON.stringify({ v: DRAFT_VERSION, savedAt: new Date().toISOString(), state });
}

/** null para JSON corrompido ou de outra versão; campos desconhecidos são descartados e os de tipo errado voltam ao vazio. */
export function restoreDossierDraft(raw: string | null | undefined): DossierFormState | null {
  if (!raw) return null;
  let parsed: unknown;
  try { parsed = JSON.parse(raw); } catch { return null; }
  const draft = parsed as { v?: unknown; state?: unknown } | null;
  if (!draft || draft.v !== DRAFT_VERSION || !draft.state || typeof draft.state !== "object" || Array.isArray(draft.state)) return null;
  const saved = draft.state as Record<string, unknown>;
  const base = emptyDossierForm();
  const restored = { ...base } as Record<string, unknown>;
  for (const [key, fallback] of Object.entries(base)) {
    const value = saved[key];
    if (Array.isArray(fallback)) restored[key] = Array.isArray(value) && value.every((item) => typeof item === "string") ? value : fallback;
    else if (typeof value === typeof fallback) restored[key] = value;
  }
  if (!["veiculo", "imovel", "outro"].includes(restored.objetoTipo as string)) restored.objetoTipo = base.objetoTipo;
  return restored as DossierFormState;
}
